import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import * as uuid from 'uuid';

import { ChatMessageTypeModel, ChatRole } from '../../../api/ai/enums/AI-chat-completion.enum';
import { ContexualKeywordIndicesResponseModel } from '../../../api/mentions/model/contexual-keyword-indices-response.model';
import { AIChatCompletionApiModel, AIChatCompletionMessageApiModel } from '../../../api/ai/model/AI-chat-completion-api.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';

import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { languageDataLocalizationMap } from '../../../Core/Localization/language-localization.model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ConfigService } from '../../../Core/Config/services/config.service';

import { IndexHighlightPipe } from '../../Misc/pipes/Highlight/indexHighlight.pipe';
import { HighlightKeywordsPipe } from '../../Misc/pipes/Highlight/highlight-keywords.pipe';
import { KeywordHighlightModel } from '../../Misc/models/keyword-highlight.model';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { getTextAlign } from '../../Misc/helpers/formatting/text.helper';
import { PendoService } from '../../Misc/services/pendo.service';
import { DropdownOptionModel } from '../../Forms/models/dropdown-option.model';

import { Mention } from '../models/mention';
import { KeywordLocationModel } from '../models/keyword-location.model';
import { TranslateService } from '../../Forms/services/translate.service';

@Injectable()
export class TranslateMentionsService extends TranslateService implements OnDestroy {

    readonly headlineStringMatch: string = 'headline';
    readonly bodyStringMatch: string = 'snippet';
    readonly summaryStringMatch: string = 'summary';

    languageString: string; // ex: English
    private languageString$ = new BehaviorSubject<string>(null);
    language: string; // ex: en
    private checkTextAlign$ = new BehaviorSubject<string>(null);
    highlightKeywords: KeywordHighlightModel[];
    concurrentTranslation: number = 0;
    private concurrentTranslation$ = new BehaviorSubject<number>(0);

    private mentionTranslationCompleteSubject = new BehaviorSubject<string>(null);
    mentionTranslationComplete$ = this.mentionTranslationCompleteSubject.asObservable();

    constructor(
        protected indexHighlightPipe: IndexHighlightPipe,
        protected highlightKeywordsPipe: HighlightKeywordsPipe,
        protected userPreferencesService: UserPreferencesService,
        private userStateService: CurrentUserStateService,
        private pendoService: PendoService,
        public localizationService: LocalizationService,
        public messageService: MessageService,
        public configService: ConfigService,
        public mentionsApi: MentionsApi,
    ) {

        super(
            localizationService,
            messageService,
            configService
        );

        this.subscriptions.push(
            this.userStateService.getDataReferenceLanguageTag().subscribe(languageTag => {
                this.language = languageTag?.split('-')?.[0];

                // just a fail safe, should never get in the if
                if (!this.language) {
                    this.language = 'en';
                }

                this.setLanguage(languageDataLocalizationMap[`${languageTag}.${this.language}`]);
            })
        );
    }

    private setLanguage(language: string) {
        this.languageString = language;

        // just a fail safe, should never get in the if
        if (!this.languageString) {
            this.languageString = 'English';
        }
        this.languageString$.next(this.languageString);
    }

    getLanguage(): Observable<string> {
        return this.languageString$;
    }

    /**
     * going from arabic to english, the text align changes, and messes up the mention,
     * so this function will checkTextAlign$.next only once there is a change and only one time, we don't want to spam it always
     */
    checkTextAlign(mention: Mention): void {
        const bodyTextAlign = getTextAlign(mention?.body);
        const translatedBodyTextAlign = getTextAlign(mention?.translation?.body);

        if (bodyTextAlign && translatedBodyTextAlign && !mention?.translation?.textDirection && (bodyTextAlign !== translatedBodyTextAlign)) {
            mention.translation.textDirection = translatedBodyTextAlign;
            this.checkTextAlign$.next(translatedBodyTextAlign);
        }
    }

    getTextAlign(): Observable<string> {
        return this.checkTextAlign$;
    }

    setDefaultTextAlign(mention: Mention) {
        if (mention?.translation?.textDirection) {
            const bodyTextAlign = getTextAlign(mention?.body);
            this.checkTextAlign$.next(bodyTextAlign);
        }
    }

    addConcurrentTranslation() {
        this.concurrentTranslation++;
        this.concurrentTranslation$.next(this.concurrentTranslation);
    }

    removeConcurrentTranslation() {
        this.concurrentTranslation--;
        this.concurrentTranslation$.next(this.concurrentTranslation);
    }

    getConcurrentTranslation(): Observable<number> {
        return this.concurrentTranslation$;
    }

    setHighLightedKeywords(highlightKeywords: KeywordHighlightModel[]) {
        this.highlightKeywords = highlightKeywords;
    }

    setupChatAI(mention: Mention, language: DropdownOptionModel, showQuickTranslate: boolean, autoSave: boolean = true) {
        const bodyTokenSize = this.tokenizer.encode(mention.body || '')?.text?.length;
        const numberOfApiCalls = Math.ceil(bodyTokenSize / this.maxTokenLimit);

        const highlightedBody = this.substitueMarkingsForTags(
            this.indexHighlightPipe.transform(mention.body, mention.contexualKeywordIndices, this.highlightKeywords)
        );

        const bodys = this.textToArray(highlightedBody, highlightedBody.length / numberOfApiCalls);
        const requestModels = this.getRequestModel(mention, bodys, language);

        this.addConcurrentTranslation();
        this.fetchData(mention, requestModels, language, showQuickTranslate, autoSave);
    }

    fetchData(
        mention: Mention,
        requestModels: AIChatCompletionApiModel[],
        language: DropdownOptionModel,
        showQuickTranslate: boolean,
        autoSave: boolean,
        aiReferenceId?: string
    ) {
        const requestModel = requestModels.shift();

        if (!requestModel) {
            return;
        }
        if (!aiReferenceId) {
            aiReferenceId = uuid.v4();
        }

        mention.translating = true;
        if (mention.translation) {
            mention.translation.failedToTranslate = false;
        }

        const uint8array = new TextDecoder();
        let generatedContent = '';

        try {
            this.pendoService.trackEvent({
                type: 'MENTION_TRANSLATE',
                data: {
                    fromLanguage: mention.language,
                    toLanguage: language ? language.label : null
                }
            });
        } catch (err) {
            // This failing should never block further code execution
            console.error(err);
        }
        fetch(this.configService.getConfig().apiUrl + '/ai/chat/completions?temperature=0.0', {
            'headers': {
                'accept': 'text/plain',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'content-type': 'application/json',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'Ai-Reference-Id': aiReferenceId
            },
            'body': JSON.stringify(requestModel),
            'method': 'POST',
            'mode': 'cors',
            'credentials': 'include'
        }).then(
            (response) => {
                if (response.status !== 200) {
                    // should give us some context as to why the call fails (at least for now)
                    console.log('response', response);
                    throw new Error;
                }
                const reader = response.body.getReader();
                generatedContent = '';
                const pump = (controller) => {
                    return reader.read().then(({ done, value }) => {
                        let nextValue = uint8array.decode(value);

                        if (!generatedContent || (generatedContent === '')) {
                            if (nextValue.trim() === '.') {
                                // Handle the case of some amount of whitespace around a .
                                nextValue = '';
                            } else {
                                // handle the case of starting white space but otherwise good content
                                nextValue = nextValue.trimStart();

                                // handle OpenAI bug that returns undesired fence code block wrapper
                                if (nextValue.startsWith('```json')) {
                                    nextValue = nextValue.replace('```json', '');
                                }

                                if (nextValue.startsWith('```html')) {
                                    nextValue = nextValue.replace('```html', '');
                                }

                                if (nextValue.startsWith('html')) {
                                    nextValue = nextValue.replace('html', '');
                                }
                            }
                        }

                        if (nextValue.endsWith('```')) {
                            nextValue = nextValue.replace('```', '');
                        }

                        generatedContent += nextValue;
                        this.setTranslated(mention, generatedContent, false, language, showQuickTranslate);

                        // When no more data needs to be consumed, close the stream
                        if (done) {
                            controller.close();
                            reader?.cancel();
                            if (requestModels.length) {
                                this.setTranslated(mention, generatedContent, true, language, showQuickTranslate);
                                this.fetchData(mention, requestModels, language, showQuickTranslate, autoSave);
                                return;
                            }
                            if (mention.mentionId && autoSave) {
                                this.saveTranslation(mention);
                            } else {
                                this.onComplete(mention);
                            }
                            return;
                        }
                        // Enqueue the next data chunk into our target stream
                        controller.enqueue(value);
                        return pump(controller);
                    });
                };
                const startFunction = (controller) => {
                    return pump(controller);
                };
                return new ReadableStream({
                    start: startFunction
                });
            }
        ).catch(
            error => {
                console.log('error with streaming', error);
                mention.translating = false;
                mention.translation = {
                    failedToTranslate: true
                };

                this.messageService.add(
                    {
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Mention.translate.error.title'),
                        detail: this.localizationService.transform('Mention.translate.error.description')
                    }
                );
                this.removeConcurrentTranslation();
            }
        );
    }

    getRequestModel(mention: Mention, bodys: string[], language: DropdownOptionModel): AIChatCompletionApiModel[] {
        let lang = this.languageString;
        if (language) {
            lang = language.value;
        }
        const messagesRequest = [];

        for (let i = 0; i < bodys.length; i++) {
            const message: AIChatCompletionMessageApiModel = {
                role: ChatRole.USER,
                chatMessageType: ChatMessageTypeModel.BASIC,
                message : 'Here is an article:\n'
            };

            if ((i === 0) && mention.headline) {
                const highlightedHeadline = this.substitueMarkingsForTags(this.highlightKeywordsPipe.transform(mention.headline, this.highlightKeywords));
                message.message += `<${this.headlineStringMatch}>${highlightedHeadline}</${this.headlineStringMatch}>`;
            }
            if (bodys[i]) {
                message.message += `<${this.bodyStringMatch}>${bodys[i]}</${this.bodyStringMatch}>`;
            }
            if ((i === bodys.length - 1) && mention.summary) {
                const highlightedSummary = this.substitueMarkingsForTags(this.highlightKeywordsPipe.transform(mention.summary, this.highlightKeywords));
                message.message += `<${this.summaryStringMatch}>${highlightedSummary}</${this.summaryStringMatch}>`;
            }

            // output format
            const messageOutput: AIChatCompletionMessageApiModel = {
                role: ChatRole.USER,
                chatMessageType: ChatMessageTypeModel.BASIC,
                message : `Translate the article to ${lang} while retaining the existing <tags>:\n`
            };

            messageOutput.message += '\nFormat the output in the following way:\n';
            if ((i === 0) && mention.headline) {
                messageOutput.message += `<${this.headlineStringMatch}>[${this.headlineStringMatch} in ${lang}]</${this.headlineStringMatch}>`;
            }
            if (bodys[i]) {
                messageOutput.message += `<${this.bodyStringMatch}>[${this.bodyStringMatch} in ${lang}]</${this.bodyStringMatch}>`;
            }
            if ((i === bodys.length - 1) && mention.summary) {
                messageOutput.message += `<${this.summaryStringMatch}>[${this.summaryStringMatch} in ${lang}]</${this.summaryStringMatch}>`;
            }

            messagesRequest.push({ messages: [message, messageOutput]} as AIChatCompletionApiModel);
        }

        return messagesRequest;
    }

    substitueMarkingsForTags(text: string): string {
        text = text.replace(/agprhighlightstart/g, '<agprhighlightstart>');
        text = text.replace(/agprhighlightend/g, '<agprhighlightend>');
        text = text.replace(/agprhighlightfilterstart/g, '<agprhighlightfilterstart>');
        text = text.replace(/agprhighlightfilterend/g, '<agprhighlightfilterend>');

        // do not want to add in hotfix because of text.replace(/<\/span>/g, '<agprhighlightend>').. but we should look into this
        // const bodyMatchCutOff = new RegExp('<span class="agpr-highlight">', 'g');
        // if (text.match(bodyMatchCutOff)?.length) {
        //     text = text.replace(/<span class="agpr-highlight">/g, '<agprhighlightstart>');
        //     text = text.replace(/<\/span>/g, '<agprhighlightend>');
        // }

        // const bodyMatchCutOffFilter = new RegExp('<span class="agpr-highlight--filter">', 'g');
        // if (text.match(bodyMatchCutOffFilter)?.length) {
        //     text = text.replace(/<span class="agpr-highlight--filter">/g, '<agprhighlightstart>');
        //     text = text.replace(/<\/span>/g, '<agprhighlightend>');
        // }

        return text;
    }

    removeHighlights(text: string): string {
        text = text.replace(/<agprhighlightstart>/g, '');
        text = text.replace(/<agprhighlightend>/g, '');
        text = text.replace(/<\/agprhighlightend>/g, '');
        text = text.replace(/<agprhighlightfilterstart>/g, '');
        text = text.replace(/<agprhighlightfilterend>/g, '');
        text = text.replace(/<\/agprhighlightfilterend>/g, '');
        text = text.replace(/<span class="agpr-highlight">/g, '');
        text = text.replace(/<span class="agpr-highlight--filter">/g, '');
        text = text.replace(/<\/span>/g, '');
        return text?.trim();
    }

    getIndexes(text: string): KeywordLocationModel[] {

        const keywordLocation: KeywordLocationModel[] = [];

        const highlightStart =
            new RegExp('<agprhighlightstart>|<agprhighlightfilterstart>|<span class="agpr-highlight">|<span class="agpr-highlight--filter">', '');
        const highlightEnd = new RegExp('<agprhighlightend>|<agprhighlightfilterend>|</agprhighlightend>|</agprhighlightfilterend>|</span>', '');

        while (text?.match(highlightStart)?.length > 0) {
            const startIndex = highlightStart.exec(text.trim())?.index;

            text = text.trim()?.replace(highlightStart, '');

            const endIndex = highlightEnd.exec(text.trim())?.index;
            text = text.trim()?.replace(highlightEnd, '');

            const test: ContexualKeywordIndicesResponseModel = {
                start: startIndex,
                end: endIndex
            };

            keywordLocation.push(new KeywordLocationModel(test));
        }
        return keywordLocation;
    }

    setTranslated(mention: Mention, translatedText: string, setPrevBody: boolean, language: DropdownOptionModel, showQuickTranslate: boolean) {
        const headlineMatch = new RegExp(`(?:<${this.headlineStringMatch}>).*?(?=\s*<\/${this.headlineStringMatch}>)`, 'gs');
        const bodyMatch = new RegExp(`(?:<${this.bodyStringMatch}>).*?(?=\s*<\/${this.bodyStringMatch}>)`, 'gs');
        const summaryMatch = new RegExp(`(?:<${this.summaryStringMatch}>).*?(?=\s*<\/${this.summaryStringMatch}>)`, 'gs');

        const headline = translatedText.trim().match(headlineMatch);
        const body = translatedText.trim().match(bodyMatch);
        const summary = translatedText.trim().match(summaryMatch);

        if (!mention.translation) {
            mention.translation = {};
        }
        // between headline tags
        if (headline?.length) {
            const headlineWithHighlight = headline[0]?.replace(`<${this.headlineStringMatch}>`, '');
            mention.translation.headlineHighlightIndexes = this.getIndexes(headlineWithHighlight);
            mention.translation.headline = this.removeHighlights(headlineWithHighlight);
        } else {
            // after headline starting tag
            const headlineMatchCutOff = new RegExp(`(?:<${this.headlineStringMatch}>).*`, 'gs');
            const headlineCutOff = translatedText.trim().match(headlineMatchCutOff);
            if (headlineCutOff?.length) {
                const headlineWithHighlight = headlineCutOff[0]?.replace(`<${this.headlineStringMatch}>`, '');
                mention.translation.headlineHighlightIndexes = this.getIndexes(headlineWithHighlight);
                mention.translation.headline = this.removeHighlights(headlineWithHighlight);
            }
        }
        // between body tags
        if (body?.length) {
            const bodyWithHighlight = body[0]?.replace(`<${this.bodyStringMatch}>`, '');
            if (mention.translation.previousBody) {
                mention.translation.bodyHighlightIndexes = this.getIndexes(mention.translation.previousBody + ' ' + bodyWithHighlight);
                mention.translation.body = this.removeHighlights(mention.translation.previousBody + ' ' + bodyWithHighlight);
            } else {
                mention.translation.bodyHighlightIndexes = this.getIndexes(bodyWithHighlight);
                mention.translation.body = this.removeHighlights(bodyWithHighlight);
            }
            if (setPrevBody) {
                if (!mention.translation.previousBody) {
                    mention.translation.previousBody = '';
                }
                mention.translation.previousBody = mention.translation.previousBody + ' ' + bodyWithHighlight;
            }
        } else {
            // after body starting tag
            const bodyMatchCutOff = new RegExp(`(?:<${this.bodyStringMatch}>).*`, 'gs');
            const bodyCutOff = translatedText.trim().match(bodyMatchCutOff);
            if (bodyCutOff?.length) {
                const bodyWithHighlight = bodyCutOff[0]?.replace(`<${this.bodyStringMatch}>`, '');
                if (mention.translation.previousBody) {
                    mention.translation.bodyHighlightIndexes = this.getIndexes(mention.translation.previousBody + ' ' + bodyWithHighlight);
                    mention.translation.body = this.removeHighlights(mention.translation.previousBody + ' ' + bodyWithHighlight);
                } else {
                    mention.translation.bodyHighlightIndexes = this.getIndexes(bodyWithHighlight);
                    mention.translation.body = this.removeHighlights(bodyWithHighlight);
                }
                if (setPrevBody) {
                    if (!mention.translation.previousBody) {
                        mention.translation.previousBody = '';
                    }
                    mention.translation.previousBody = mention.translation.previousBody + ' ' + bodyWithHighlight;
                }
            }
        }
        // between summary tags
        if (summary?.length) {
            const summaryWithHighlight = summary[0]?.replace(`<${this.summaryStringMatch}>`, '');
            mention.translation.summaryHighlightIndexes = this.getIndexes(summaryWithHighlight);
            mention.translation.summary = this.removeHighlights(summaryWithHighlight);
        } else {
            // after summary starting tag
            const summaryMatchCutOff = new RegExp(`(?:<${this.summaryStringMatch}>).*`, 'gs');
            const summaryCutOff = translatedText.trim().match(summaryMatchCutOff);
            if (summaryCutOff?.length) {
                const summaryWithHighlight = summaryCutOff[0]?.replace(`<${this.summaryStringMatch}>`, '');
                mention.translation.summaryHighlightIndexes = this.getIndexes(summaryWithHighlight);
                mention.translation.summary = this.removeHighlights(summaryWithHighlight);
            }
        }

        let langISO = this.language;
        mention.translation.languageString = null;
        if (language) {
            if (language.subLabel) {
                langISO = language.subLabel;
            } else {
                langISO = null;
                mention.translation.languageString = language.label;
            }
        }
        mention.translation.language = langISO;

        mention.showTranslated = showQuickTranslate;

        if (mention?.translation?.body) {
            if (this.highlightKeywords) {
                const highlightedBody = this.indexHighlightPipe.transform(
                    mention?.translation?.body,
                    mention.translation.bodyHighlightIndexes,
                    this.highlightKeywords
                );
                mention.translation.paragraphs = (highlightedBody || '').split('\n\n');
            } else {
                mention.translation.paragraphs = (mention?.translation?.body || '').split('\n\n');
            }
            mention.translation.summaryParagraphs = (mention?.translation?.summary || '').split('\n\n');
        }
        this.checkTextAlign(mention);
    }

    /**
     * NOTE: not being used at the moment, but maybe down the road we will want to
     * setMaxLimitOnBodyText
     * this function is to trim the body text, if it's too long (we don't want to hit the max token limit)
     * it will loop through, and keep triming until the token limit is matched, currently it removes 100 characters from the end at a time.
     */
    setMaxLimitOnBodyText(mention: Mention): string {
        let body = mention.body;
        while (this.tokenizer.encode(body)?.text?.length > (this.maxTokenLimit)) {
            body = body.slice(0, -100);
        }
        return body;
    }

    saveTranslation(mention: Mention) {

        this.mentionsApi.patchMention(mention.toUpdateMentionTranslationApiModel()).subscribe({
            next: () => {
                this.onComplete(mention);
            },
            error: () => {

                this.messageService.add(
                    {
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Mention.translate.save.error.title'),
                        detail: this.localizationService.transform('Mention.translate.save.error.description')
                    }
                );
                this.onComplete(mention);

            }
        });
    }

    onComplete(mention: Mention) {
        mention.translating = false;
        if (mention.id) {
            this.mentionTranslationCompleteSubject.next(mention.id);
        } else if (mention.mentionId) {
            this.mentionTranslationCompleteSubject.next(mention.mentionId);
        }

        delete mention.translation.previousBody;
        this.removeConcurrentTranslation();
    }
}
